import type { Match } from "@/lib/types";

/** Returns true when ANY slot of the match has a ready video. Used
 * by the per-card "▸ Watch" affordance. */
export function matchHasReadyVideo(match: Match): boolean {
  const statuses = Object.values(match.video_status || {});
  return statuses.some((s) => s === "ready");
}

/** Pick the first ready slot for a match (deterministic for use
 * in deep-links from a match card to the game view). Returns null
 * when no slot is ready. Order: full → first_half → second_half. */
export function firstReadySlot(match: Match): string | null {
  const order = [
    "full",
    "first_half",
    "second_half",
    "extra_time",
    "penalty_shootout",
  ];
  for (const slot of order) {
    if (match.video_status?.[slot] === "ready") return slot;
  }
  // Fall back to anything else marked ready (forward-compat for
  // future slot kinds).
  for (const [slot, status] of Object.entries(match.video_status ?? {})) {
    if (status === "ready") return slot;
  }
  return null;
}

/** Date-sort matches descending (most recent first), then by
 * created_at as a secondary key for stability across matches with
 * the same date. Empty / unparseable dates sink to the bottom. */
export function sortMatchesByDateDesc(matches: Match[]): Match[] {
  return [...matches].sort((a, b) => {
    const aKey = a.date || "";
    const bKey = b.date || "";
    if (aKey && !bKey) return -1;
    if (!aKey && bKey) return 1;
    if (aKey !== bKey) return aKey > bKey ? -1 : 1;
    return (b.created_at || "").localeCompare(a.created_at || "");
  });
}

/** Returns true when the match has a recorded score (both home +
 * away scores are non-null). Upcoming matches have null scores.
 * Used to gate the score-reveal pill. */
export function hasRecordedScore(match: Match): boolean {
  return match.score_home != null && match.score_away != null;
}

/** Aggregate processing progress across all slots — used for the
 * "PROCESSING" badge on a match card. Returns null when no slot
 * is being transcoded (badge hidden).
 *
 * The backend's video-status enum is `transcoding` (see
 * `routers/matches.py` + `routers/uploads.py`); the original M11a
 * port read `processing` which never matched. Reviewer-flagged on
 * PR #21 P2. */
export function processingProgress(match: Match): number | null {
  const statuses = Object.values(match.video_status || {});
  const processing = statuses.filter((s) => s === "transcoding").length;
  if (processing === 0) return null;
  // No per-slot progress on the bare match list — the badge just
  // says "PROCESSING" without a percentage. Returning 0 is the
  // signal "show the badge with no numeric progress". The admin
  // pages have richer progress data via /api/transcode-progress.
  return 0;
}
